import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  GeoAnalysisSection,
  type GeoAnalysisSectionTranslations,
  type ExtraTab,
  type AltimetryData,
} from '../../../GeoAnalysisSection'
import { useAnalysisMode, useAnalysisServices } from '../../contexts'
import { useAnalysisMapSectionData } from '../../contexts/MapSectionDataContext'

interface GeoAnalysisTabProps {
  extraTabs?: ExtraTab[]
  /**
   * LiDAR-derived altimetry. When present (parcel has point-cloud coverage), it makes the point
   * cloud authoritative for the terrain slope & elevation numbers; null ⇒ API altimetry only.
   */
  terrainAltimetryData?: AltimetryData | null
}

export const GeoAnalysisTab: React.FC<GeoAnalysisTabProps> = ({
  extraTabs,
  terrainAltimetryData = null,
}) => {
  const { t } = useTranslation('geoAnalysis')
  const { entity, readOnly } = useAnalysisMode()
  const { getSectionData } = useAnalysisServices()
  const { setActiveSection } = useAnalysisMapSectionData()

  // Geo analysis has no map layer — clear active section
  useEffect(() => {
    setActiveSection(null)
  }, [setActiveSection])

  // Get altimetry data: from entity in readOnly, from services in main app
  const geoAnalysisData = readOnly
    ? entity?.geoAnalysis
    : getSectionData('geoAnalysis')
  const altimetryData = geoAnalysisData?.altimetryData || null
  const altimetryLoading = readOnly
    ? false
    : getSectionData('geoAnalysisLoading') || false

  // Build translations object for GeoAnalysisSection
  const translations: GeoAnalysisSectionTranslations = useMemo(
    () => ({
      loading: t('loading'),
      error: t('error'),
      noData: t('noData'),
      noAltitudeData: t('noAltitudeData'),
      noSlopeAnalysis: t('noSlopeAnalysis'),
      noPolygonData: t('noPolygonData'),
      noElevationData: t('noElevationData'),
      plotTooSmall: t('plotTooSmall'),
      plotTooSmallDescription: t('plotTooSmallDescription'),
      tabs: {
        overview: t('tabs.overview'),
        topography: t('tabs.topography'),
        shape: t('tabs.shape'),
        analysis: t('tabs.analysis'),
      },
      header: {
        title: t('header.title'),
        subtitle: t('header.subtitle'),
        scoreLabel: t('header.scoreLabel'),
        scoreTooltip: t('header.scoreTooltip'),
      },
      status: {
        realData: t('status.realData'),
        loading: t('status.loading'),
        noData: t('status.noData'),
        excellent: t('status.excellent'),
        good: t('status.good'),
        moderate: t('status.moderate'),
        challenging: t('status.challenging'),
        poor: t('status.poor'),
        regular: t('status.regular'),
        irregular: t('status.irregular'),
        neutral: t('status.neutral'),
        na: t('status.na'),
      },
      directions: {
        north: t('directions.north'),
        northEast: t('directions.northEast'),
        east: t('directions.east'),
        southEast: t('directions.southEast'),
        south: t('directions.south'),
        southWest: t('directions.southWest'),
        west: t('directions.west'),
        northWest: t('directions.northWest'),
        flat: t('directions.flat'),
        eastWest: t('directions.eastWest'),
        northSouth: t('directions.northSouth'),
        square: t('directions.square'),
      },
      slopeRanges: {
        flat: t('slopeRanges.flat'),
        gentle: t('slopeRanges.gentle'),
        moderate: t('slopeRanges.moderate'),
        steep: t('slopeRanges.steep'),
        verysteep: t('slopeRanges.verysteep'),
        range0_5: t('slopeRanges.range0_5'),
        range5_10: t('slopeRanges.range5_10'),
        range10_15: t('slopeRanges.range10_15'),
        range15_25: t('slopeRanges.range15_25'),
        rangeOver25: t('slopeRanges.rangeOver25'),
      },
      charts: {
        xAxis: t('charts.xAxis'),
        yAxis: t('charts.yAxis'),
        altitude: t('charts.altitude'),
        meters: t('charts.meters'),
        position: t('charts.position'),
        status: t('charts.status'),
        flatArea: t('charts.flatArea'),
        slopedArea: t('charts.slopedArea'),
        plotBoundary: t('charts.plotBoundary'),
        distance: t('charts.distance'),
      },
      sections: {
        geometryAnalysis: t('sections.geometryAnalysis'),
        altitudeProfile: t('sections.altitudeProfile'),
        developmentAnalysis: t('sections.developmentAnalysis'),
        topographicVisualization: t('sections.topographicVisualization'),
        flatAreaAnalysis: t('sections.flatAreaAnalysis'),
        plotBoundaryVisualization: t('sections.plotBoundaryVisualization'),
        shapeMetrics: t('sections.shapeMetrics'),
        slopeDistribution: t('sections.slopeDistribution'),
        orientationAnalysis: t('sections.orientationAnalysis'),
        slopeSunOrientation: t('sections.slopeSunOrientation'),
      },
      metrics: {
        totalArea: t('metrics.totalArea'),
        perimeter: t('metrics.perimeter'),
        averageSlope: t('metrics.averageSlope'),
        dominantOrientation: t('metrics.dominantOrientation'),
        shapeRegularity: t('metrics.shapeRegularity'),
        compactnessScore: t('metrics.compactnessScore'),
        lengthWidth: t('metrics.lengthWidth'),
        minimum: t('metrics.minimum'),
        maximum: t('metrics.maximum'),
        elevationRange: t('metrics.elevationRange'),
        dataPoints: t('metrics.dataPoints'),
        maximumSlope: t('metrics.maximumSlope'),
        maximumFlatArea: t('metrics.maximumFlatArea'),
        constructionSuitability: t('metrics.constructionSuitability'),
        minAltitude: t('metrics.minAltitude'),
        maxAltitude: t('metrics.maxAltitude'),
        maxFlatArea: t('metrics.maxFlatArea'),
        percentOfTotal: t('metrics.percentOfTotal'),
        slopeThreshold: t('metrics.slopeThreshold'),
        shapeFactor: t('metrics.shapeFactor'),
        regularity: t('metrics.regularity'),
        compactness: t('metrics.compactness'),
        dimensions: t('metrics.dimensions'),
        areaEfficiency: t('metrics.areaEfficiency'),
        buildable: t('metrics.buildable'),
        gridSize: t('metrics.gridSize'),
        faces: t('metrics.faces'),
        sunExposure: t('metrics.sunExposure'),
      },
      sunExposureNotes: {
        excellent: t('sunExposureNotes.excellent'),
        moderate: t('sunExposureNotes.moderate'),
        poor: t('sunExposureNotes.poor'),
        neutral: t('sunExposureNotes.neutral'),
      },
      tooltips: {
        totalArea: {
          title: t('tooltips.totalArea.title'),
          shortDescription: t('tooltips.totalArea.shortDescription'),
          calculation: t('tooltips.totalArea.calculation'),
          importance: t('tooltips.totalArea.importance'),
          limitations: t('tooltips.totalArea.limitations'),
          source: t('tooltips.totalArea.source'),
        },
        perimeter: {
          title: t('tooltips.perimeter.title'),
          shortDescription: t('tooltips.perimeter.shortDescription'),
          calculation: t('tooltips.perimeter.calculation'),
          importance: t('tooltips.perimeter.importance'),
          limitations: t('tooltips.perimeter.limitations'),
          source: t('tooltips.perimeter.source'),
        },
        averageSlope: {
          title: t('tooltips.averageSlope.title'),
          shortDescription: t('tooltips.averageSlope.shortDescription'),
          calculation: t('tooltips.averageSlope.calculation'),
          importance: t('tooltips.averageSlope.importance'),
          limitations: t('tooltips.averageSlope.limitations'),
          source: t('tooltips.averageSlope.source'),
          categories: [
            {
              label: t('tooltips.averageSlope.categories.0.label'),
              range: t('tooltips.averageSlope.categories.0.range'),
              description: t('tooltips.averageSlope.categories.0.description'),
            },
            {
              label: t('tooltips.averageSlope.categories.1.label'),
              range: t('tooltips.averageSlope.categories.1.range'),
              description: t('tooltips.averageSlope.categories.1.description'),
            },
            {
              label: t('tooltips.averageSlope.categories.2.label'),
              range: t('tooltips.averageSlope.categories.2.range'),
              description: t('tooltips.averageSlope.categories.2.description'),
            },
            {
              label: t('tooltips.averageSlope.categories.3.label'),
              range: t('tooltips.averageSlope.categories.3.range'),
              description: t('tooltips.averageSlope.categories.3.description'),
            },
          ],
        },
        dominantOrientation: {
          title: t('tooltips.dominantOrientation.title'),
          shortDescription: t('tooltips.dominantOrientation.shortDescription'),
          calculation: t('tooltips.dominantOrientation.calculation'),
          importance: t('tooltips.dominantOrientation.importance'),
          limitations: t('tooltips.dominantOrientation.limitations'),
          source: t('tooltips.dominantOrientation.source'),
          categories: [
            {
              label: t('tooltips.dominantOrientation.categories.0.label'),
              description: t(
                'tooltips.dominantOrientation.categories.0.description'
              ),
            },
            {
              label: t('tooltips.dominantOrientation.categories.1.label'),
              description: t(
                'tooltips.dominantOrientation.categories.1.description'
              ),
            },
            {
              label: t('tooltips.dominantOrientation.categories.2.label'),
              description: t(
                'tooltips.dominantOrientation.categories.2.description'
              ),
            },
            {
              label: t('tooltips.dominantOrientation.categories.3.label'),
              description: t(
                'tooltips.dominantOrientation.categories.3.description'
              ),
            },
            {
              label: t('tooltips.dominantOrientation.categories.4.label'),
              description: t(
                'tooltips.dominantOrientation.categories.4.description'
              ),
            },
          ],
        },
        shapeRegularity: {
          title: t('tooltips.shapeRegularity.title'),
          shortDescription: t('tooltips.shapeRegularity.shortDescription'),
          calculation: t('tooltips.shapeRegularity.calculation'),
          importance: t('tooltips.shapeRegularity.importance'),
          limitations: t('tooltips.shapeRegularity.limitations'),
          source: t('tooltips.shapeRegularity.source'),
          categories: [
            {
              label: t('tooltips.shapeRegularity.categories.0.label'),
              range: t('tooltips.shapeRegularity.categories.0.range'),
              description: t(
                'tooltips.shapeRegularity.categories.0.description'
              ),
            },
            {
              label: t('tooltips.shapeRegularity.categories.1.label'),
              range: t('tooltips.shapeRegularity.categories.1.range'),
              description: t(
                'tooltips.shapeRegularity.categories.1.description'
              ),
            },
          ],
        },
        compactnessScore: {
          title: t('tooltips.compactnessScore.title'),
          shortDescription: t('tooltips.compactnessScore.shortDescription'),
          calculation: t('tooltips.compactnessScore.calculation'),
          importance: t('tooltips.compactnessScore.importance'),
          limitations: t('tooltips.compactnessScore.limitations'),
          source: t('tooltips.compactnessScore.source'),
          categories: [
            {
              label: t('tooltips.compactnessScore.categories.0.label'),
              range: t('tooltips.compactnessScore.categories.0.range'),
              description: t(
                'tooltips.compactnessScore.categories.0.description'
              ),
            },
            {
              label: t('tooltips.compactnessScore.categories.1.label'),
              range: t('tooltips.compactnessScore.categories.1.range'),
              description: t(
                'tooltips.compactnessScore.categories.1.description'
              ),
            },
            {
              label: t('tooltips.compactnessScore.categories.2.label'),
              range: t('tooltips.compactnessScore.categories.2.range'),
              description: t(
                'tooltips.compactnessScore.categories.2.description'
              ),
            },
          ],
        },
        maximumSlope: {
          title: t('tooltips.maximumSlope.title'),
          shortDescription: t('tooltips.maximumSlope.shortDescription'),
          calculation: t('tooltips.maximumSlope.calculation'),
          importance: t('tooltips.maximumSlope.importance'),
          limitations: t('tooltips.maximumSlope.limitations'),
          source: t('tooltips.maximumSlope.source'),
          categories: [
            {
              label: t('tooltips.maximumSlope.categories.0.label'),
              range: t('tooltips.maximumSlope.categories.0.range'),
              description: t('tooltips.maximumSlope.categories.0.description'),
            },
            {
              label: t('tooltips.maximumSlope.categories.1.label'),
              range: t('tooltips.maximumSlope.categories.1.range'),
              description: t('tooltips.maximumSlope.categories.1.description'),
            },
            {
              label: t('tooltips.maximumSlope.categories.2.label'),
              range: t('tooltips.maximumSlope.categories.2.range'),
              description: t('tooltips.maximumSlope.categories.2.description'),
            },
            {
              label: t('tooltips.maximumSlope.categories.3.label'),
              range: t('tooltips.maximumSlope.categories.3.range'),
              description: t('tooltips.maximumSlope.categories.3.description'),
            },
          ],
        },
        constructionSuitability: {
          title: t('tooltips.constructionSuitability.title'),
          shortDescription: t(
            'tooltips.constructionSuitability.shortDescription'
          ),
          calculation: t('tooltips.constructionSuitability.calculation'),
          importance: t('tooltips.constructionSuitability.importance'),
          limitations: t('tooltips.constructionSuitability.limitations'),
          source: t('tooltips.constructionSuitability.source'),
          categories: [
            {
              label: t('tooltips.constructionSuitability.categories.0.label'),
              range: t('tooltips.constructionSuitability.categories.0.range'),
              description: t(
                'tooltips.constructionSuitability.categories.0.description'
              ),
            },
            {
              label: t('tooltips.constructionSuitability.categories.1.label'),
              range: t('tooltips.constructionSuitability.categories.1.range'),
              description: t(
                'tooltips.constructionSuitability.categories.1.description'
              ),
            },
            {
              label: t('tooltips.constructionSuitability.categories.2.label'),
              range: t('tooltips.constructionSuitability.categories.2.range'),
              description: t(
                'tooltips.constructionSuitability.categories.2.description'
              ),
            },
            {
              label: t('tooltips.constructionSuitability.categories.3.label'),
              range: t('tooltips.constructionSuitability.categories.3.range'),
              description: t(
                'tooltips.constructionSuitability.categories.3.description'
              ),
            },
          ],
        },
        flatAreaAnalysis: {
          title: t('tooltips.flatAreaAnalysis.title'),
          shortDescription: t('tooltips.flatAreaAnalysis.shortDescription'),
          calculation: t('tooltips.flatAreaAnalysis.calculation'),
          importance: t('tooltips.flatAreaAnalysis.importance'),
          limitations: t('tooltips.flatAreaAnalysis.limitations'),
          source: t('tooltips.flatAreaAnalysis.source'),
          categories: [
            {
              label: t('tooltips.flatAreaAnalysis.categories.0.label'),
              description: t(
                'tooltips.flatAreaAnalysis.categories.0.description'
              ),
            },
            {
              label: t('tooltips.flatAreaAnalysis.categories.1.label'),
              description: t(
                'tooltips.flatAreaAnalysis.categories.1.description'
              ),
            },
          ],
        },
        maxFlatArea: {
          title: t('tooltips.maxFlatArea.title'),
          shortDescription: t('tooltips.maxFlatArea.shortDescription'),
          calculation: t('tooltips.maxFlatArea.calculation'),
          importance: t('tooltips.maxFlatArea.importance'),
          limitations: t('tooltips.maxFlatArea.limitations'),
          source: t('tooltips.maxFlatArea.source'),
          categories: [
            {
              label: t('tooltips.maxFlatArea.categories.0.label'),
              description: t('tooltips.maxFlatArea.categories.0.description'),
            },
            {
              label: t('tooltips.maxFlatArea.categories.1.label'),
              description: t('tooltips.maxFlatArea.categories.1.description'),
            },
            {
              label: t('tooltips.maxFlatArea.categories.2.label'),
              description: t('tooltips.maxFlatArea.categories.2.description'),
            },
            {
              label: t('tooltips.maxFlatArea.categories.3.label'),
              description: t('tooltips.maxFlatArea.categories.3.description'),
            },
            {
              label: t('tooltips.maxFlatArea.categories.4.label'),
              description: t('tooltips.maxFlatArea.categories.4.description'),
            },
          ],
        },
        shapeFactor: {
          title: t('tooltips.shapeFactor.title'),
          shortDescription: t('tooltips.shapeFactor.shortDescription'),
          calculation: t('tooltips.shapeFactor.calculation'),
          importance: t('tooltips.shapeFactor.importance'),
          limitations: t('tooltips.shapeFactor.limitations'),
          source: t('tooltips.shapeFactor.source'),
          categories: [
            {
              label: t('tooltips.shapeFactor.categories.0.label'),
              range: t('tooltips.shapeFactor.categories.0.range'),
              description: t('tooltips.shapeFactor.categories.0.description'),
            },
            {
              label: t('tooltips.shapeFactor.categories.1.label'),
              range: t('tooltips.shapeFactor.categories.1.range'),
              description: t('tooltips.shapeFactor.categories.1.description'),
            },
            {
              label: t('tooltips.shapeFactor.categories.2.label'),
              range: t('tooltips.shapeFactor.categories.2.range'),
              description: t('tooltips.shapeFactor.categories.2.description'),
            },
          ],
        },
        regularity: {
          title: t('tooltips.regularity.title'),
          shortDescription: t('tooltips.regularity.shortDescription'),
          calculation: t('tooltips.regularity.calculation'),
          importance: t('tooltips.regularity.importance'),
          limitations: t('tooltips.regularity.limitations'),
          source: t('tooltips.regularity.source'),
          categories: [
            {
              label: t('tooltips.regularity.categories.0.label'),
              range: t('tooltips.regularity.categories.0.range'),
              description: t('tooltips.regularity.categories.0.description'),
            },
            {
              label: t('tooltips.regularity.categories.1.label'),
              range: t('tooltips.regularity.categories.1.range'),
              description: t('tooltips.regularity.categories.1.description'),
            },
            {
              label: t('tooltips.regularity.categories.2.label'),
              range: t('tooltips.regularity.categories.2.range'),
              description: t('tooltips.regularity.categories.2.description'),
            },
          ],
        },
        compactness: {
          title: t('tooltips.compactness.title'),
          shortDescription: t('tooltips.compactness.shortDescription'),
          calculation: t('tooltips.compactness.calculation'),
          importance: t('tooltips.compactness.importance'),
          limitations: t('tooltips.compactness.limitations'),
          source: t('tooltips.compactness.source'),
          categories: [
            {
              label: t('tooltips.compactness.categories.0.label'),
              range: t('tooltips.compactness.categories.0.range'),
              description: t('tooltips.compactness.categories.0.description'),
            },
            {
              label: t('tooltips.compactness.categories.1.label'),
              range: t('tooltips.compactness.categories.1.range'),
              description: t('tooltips.compactness.categories.1.description'),
            },
            {
              label: t('tooltips.compactness.categories.2.label'),
              range: t('tooltips.compactness.categories.2.range'),
              description: t('tooltips.compactness.categories.2.description'),
            },
            {
              label: t('tooltips.compactness.categories.3.label'),
              range: t('tooltips.compactness.categories.3.range'),
              description: t('tooltips.compactness.categories.3.description'),
            },
          ],
        },
        dimensions: {
          title: t('tooltips.dimensions.title'),
          shortDescription: t('tooltips.dimensions.shortDescription'),
          calculation: t('tooltips.dimensions.calculation'),
          importance: t('tooltips.dimensions.importance'),
          limitations: t('tooltips.dimensions.limitations'),
          source: t('tooltips.dimensions.source'),
          categories: [
            {
              label: t('tooltips.dimensions.categories.0.label'),
              description: t('tooltips.dimensions.categories.0.description'),
            },
            {
              label: t('tooltips.dimensions.categories.1.label'),
              description: t('tooltips.dimensions.categories.1.description'),
            },
            {
              label: t('tooltips.dimensions.categories.2.label'),
              description: t('tooltips.dimensions.categories.2.description'),
            },
            {
              label: t('tooltips.dimensions.categories.3.label'),
              description: t('tooltips.dimensions.categories.3.description'),
            },
            {
              label: t('tooltips.dimensions.categories.4.label'),
              description: t('tooltips.dimensions.categories.4.description'),
            },
          ],
        },
        areaEfficiency: {
          title: t('tooltips.areaEfficiency.title'),
          shortDescription: t('tooltips.areaEfficiency.shortDescription'),
          calculation: t('tooltips.areaEfficiency.calculation'),
          importance: t('tooltips.areaEfficiency.importance'),
          limitations: t('tooltips.areaEfficiency.limitations'),
          source: t('tooltips.areaEfficiency.source'),
          categories: [
            {
              label: t('tooltips.areaEfficiency.categories.0.label'),
              range: t('tooltips.areaEfficiency.categories.0.range'),
              description: t(
                'tooltips.areaEfficiency.categories.0.description'
              ),
            },
            {
              label: t('tooltips.areaEfficiency.categories.1.label'),
              range: t('tooltips.areaEfficiency.categories.1.range'),
              description: t(
                'tooltips.areaEfficiency.categories.1.description'
              ),
            },
            {
              label: t('tooltips.areaEfficiency.categories.2.label'),
              range: t('tooltips.areaEfficiency.categories.2.range'),
              description: t(
                'tooltips.areaEfficiency.categories.2.description'
              ),
            },
            {
              label: t('tooltips.areaEfficiency.categories.3.label'),
              range: t('tooltips.areaEfficiency.categories.3.range'),
              description: t(
                'tooltips.areaEfficiency.categories.3.description'
              ),
            },
          ],
        },
        slopeDistribution: {
          title: t('tooltips.slopeDistribution.title'),
          shortDescription: t('tooltips.slopeDistribution.shortDescription'),
          calculation: t('tooltips.slopeDistribution.calculation'),
          importance: t('tooltips.slopeDistribution.importance'),
          limitations: t('tooltips.slopeDistribution.limitations'),
          source: t('tooltips.slopeDistribution.source'),
          categories: [
            {
              label: t('tooltips.slopeDistribution.categories.0.label'),
              range: t('tooltips.slopeDistribution.categories.0.range'),
              description: t(
                'tooltips.slopeDistribution.categories.0.description'
              ),
            },
            {
              label: t('tooltips.slopeDistribution.categories.1.label'),
              range: t('tooltips.slopeDistribution.categories.1.range'),
              description: t(
                'tooltips.slopeDistribution.categories.1.description'
              ),
            },
            {
              label: t('tooltips.slopeDistribution.categories.2.label'),
              range: t('tooltips.slopeDistribution.categories.2.range'),
              description: t(
                'tooltips.slopeDistribution.categories.2.description'
              ),
            },
            {
              label: t('tooltips.slopeDistribution.categories.3.label'),
              range: t('tooltips.slopeDistribution.categories.3.range'),
              description: t(
                'tooltips.slopeDistribution.categories.3.description'
              ),
            },
          ],
        },
      },
    }),
    [t]
  )

  if (!entity) {
    return null
  }

  return (
    <GeoAnalysisSection
      land={entity}
      loading={readOnly ? false : getSectionData('loading') || false}
      error={readOnly ? null : getSectionData('error') || null}
      altimetryData={altimetryData}
      terrainAltimetryData={terrainAltimetryData}
      altimetryLoading={altimetryLoading}
      translations={translations}
      extraTabs={extraTabs}
    />
  )
}
